import { useMemo } from 'react';
import { GLCodeDropdownData, GlCodesObject } from 'src/services/useGlCodesApi';
import { GLPeriods } from 'src/services/useGLPeriodApi';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import { InvoiceGlBreakoutItem } from 'src/services/useInvoiceGlBreakoutApi';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { InvoiceBudgetPeriodResponse } from '../InvoiceDetailsContext';
import useApIdErrors from './useApIdErrors';
import useInvoiceErrors from './useInvoiceErrors';
import useInvoiceGlBreakoutErrors, {
  GlBreakOutError,
  GlBreakOutWarning,
} from './useInvoiceGlBreakoutErrors';
import useInvoiceLineItemErrors, {
  InvoiceLineItemError,
  InvoiceLineItemInfo,
  InvoiceLineItemWarning,
} from './useInvoiceLineItemErrors';
import useInvoiceNumberWarnings from './useInvoiceNumberWarnings';
import useInvoiceWarnings from './useInvoiceWarnings';
import usePeriodStatusErrors from './usePeriodStatusErrors';
import useSupplierNameErrors from './useSupplierNameErrors';

export interface InvoiceDetailsErrors {
  invoiceNumber: string[];
  apId: string[];
  externalSupplierName: string[];
  receivedDate: string[];
  invoiceDate: string[];
  discountAmt: string[];
  totalCostAmt: string[];
  shipCostAmt: string[];
  salesTaxAmt: string[];
  notes: string[];
  lineItems: InvoiceLineItemError[];
  glBreakout: GlBreakOutError[];
}

export interface InvoiceDetailsWarnings {
  accountingPeriod?: string[];
  apId: string[];
  externalSupplierName: string[];
  discountAmt: string[];
  totalCostAmt: string[];
  shipCostAmt: string[];
  salesTaxAmt: string[];
  lineItems: InvoiceLineItemWarning[];
  glBreakout: GlBreakOutWarning[];
}

export interface InvoiceDetailsInformations {
  lineItems: InvoiceLineItemInfo[];
  invoiceNumber: string[];
}
const initialErrorState = {
  invoiceNumber: [],
  apId: [],
  externalSupplierName: [],
  receivedDate: [],
  invoiceDate: [],
  discountAmt: [],
  totalCostAmt: [],
  shipCostAmt: [],
  salesTaxAmt: [],
  notes: [],
  lineItems: [],
  glBreakout: [],
  accountingPeriod: [],
};
const intialInvoiceDetailsWarningState = {
  apId: [],
  externalSupplierName: [],
  discountAmt: [],
  totalCostAmt: [],
  shipCostAmt: [],
  salesTaxAmt: [],
  lineItems: [],
  glBreakout: [],
};

const initialInvoiceDetailsInformationsState = {
  lineItems: [],
  invoiceNumber: [],
};

const useInvoiceDetailsErrors = (
  invoice: InvoiceDetailsResponse,
  totalsGlCodeItem: InvoiceGlBreakoutItem,
  lineItems: InvoiceLineItem[],
  glBreakout: InvoiceGlBreakoutItem[],
  isLoading: boolean,
  isInProgress: boolean,
  isInvoiceEditDisabled: boolean,
  glCodeOptions: GLCodeDropdownData,
  isCreditMemo: boolean,
  initialInvoiceNumber: string,
  invoiceBudgetPeriods: InvoiceBudgetPeriodResponse[],
  glPeriods: GLPeriods,
  glCodesObject: GlCodesObject
) => {
  const {
    info: invoiceNumberInfo,
    errors: invoiceNumberErrors,
    hasDuplicateInvoiceNumber,
    isValidatingDuplicateInvoiceNumber,
  } = useInvoiceNumberWarnings(invoice, initialInvoiceNumber);
  const { apIdErrors, apIdWarnings, setApIdDirty } = useApIdErrors(invoice, isInvoiceEditDisabled);
  const { supplierErrors, supplierWarnings, setSupplierNameDirty } = useSupplierNameErrors(
    invoice,
    isInvoiceEditDisabled
  );
  const invoiceErrors = useInvoiceErrors(invoice, isCreditMemo);

  const {
    errors: lineItemErrors,
    warnings: lineItemWarnings,
    infos: lineItemInfos,
  } = useInvoiceLineItemErrors(
    lineItems,
    isInProgress,
    glCodeOptions,
    invoiceBudgetPeriods,
    isInvoiceEditDisabled,
    glCodesObject
  );

  const { errors: glBreakoutErrors, warnings: glBreakoutWarnings } =
    useInvoiceGlBreakoutErrors(glBreakout);

  const invoiceWarnings = useInvoiceWarnings(invoice, totalsGlCodeItem, isLoading);
  const { periodClosedErrors } = usePeriodStatusErrors(invoiceBudgetPeriods, glPeriods);
  const errors: InvoiceDetailsErrors = useMemo(() => {
    if (isInvoiceEditDisabled) {
      return { ...initialErrorState, lineItems: lineItemErrors };
    } else {
      return {
        invoiceNumber: invoiceNumberErrors,
        apId: apIdErrors,
        externalSupplierName: supplierErrors,
        lineItems: lineItemErrors,
        glBreakout: glBreakoutErrors,
        ...invoiceErrors,
      };
    }
  }, [
    apIdErrors,
    glBreakoutErrors,
    invoiceErrors,
    invoiceNumberErrors,
    isInvoiceEditDisabled,
    lineItemErrors,
    supplierErrors,
  ]);

  const warnings: InvoiceDetailsWarnings = useMemo(() => {
    if (isInvoiceEditDisabled) {
      return { ...intialInvoiceDetailsWarningState, lineItems: lineItemWarnings };
    } else {
      return {
        apId: apIdWarnings,
        externalSupplierName: supplierWarnings,
        lineItems: lineItemWarnings,
        glBreakout: glBreakoutWarnings,
        accountingPeriod: periodClosedErrors,

        ...invoiceWarnings,
      };
    }
  }, [
    apIdWarnings,
    glBreakoutWarnings,
    invoiceWarnings,
    isInvoiceEditDisabled,
    lineItemWarnings,
    periodClosedErrors,
    supplierWarnings,
  ]);

  const informations: InvoiceDetailsInformations = useMemo(() => {
    if (isInvoiceEditDisabled) {
      return { ...initialInvoiceDetailsInformationsState, lineItems: lineItemInfos };
    } else {
      return { lineItems: lineItemInfos, invoiceNumber: invoiceNumberInfo };
    }
  }, [invoiceNumberInfo, isInvoiceEditDisabled, lineItemInfos]);

  const warningList: string[] = [
    ...Object?.values(warnings)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];
  const informationList: string[] = [
    ...Object?.values(informations)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const errorList: string[] = [
    ...Object.values(errors)
      .map((i) => Object.values(i)?.flat())
      .flat()
      .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
      .flat()
      .filter((item: any, i, arr) => arr.indexOf(item) === i),
  ] as string[];

  const hasWarnings = useMemo(() => !!warningList.length, [warningList.length]);
  const hasErrors = useMemo(() => !!errorList.length, [errorList.length]);

  return {
    errors,
    errorList,
    informationList,
    warningList,
    informations,
    warnings,
    setApIdDirty,
    setSupplierNameDirty,
    hasErrors,
    hasWarnings,
    hasDuplicateInvoiceNumber,
    lineItemInfos,
    isValidatingDuplicateInvoiceNumber,
  };
};

export default useInvoiceDetailsErrors;
